import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _div;
import React, { useRef, useState } from 'react';
import clsx from 'clsx';
import { SubmitButton } from "../submit-button/submit-button";
import { HanaPromptTextareaV2 } from "./hana-prompt-textarea-v2";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export var PromptTextareaV2 = function PromptTextareaV2(_ref) {
  var _ref$placeholder = _ref.placeholder,
    placeholder = _ref$placeholder === void 0 ? '' : _ref$placeholder,
    cta = _ref.cta,
    _ref$categories = _ref.categories,
    categories = _ref$categories === void 0 ? [] : _ref$categories,
    className = _ref.className,
    _ref$promptClassName = _ref.promptClassName,
    promptClassName = _ref$promptClassName === void 0 ? 'tw-max-h-8' : _ref$promptClassName,
    defaultPrompt = _ref.defaultPrompt,
    selectedTopic = _ref.selectedTopic,
    onSubmitPrompt = _ref.onSubmitPrompt,
    onChangePrompt = _ref.onChangePrompt,
    customPlaceholders = _ref.customPlaceholders,
    isPromptSubmitLoading = _ref.isPromptSubmitLoading,
    initialSuggestedPrompts = _ref.initialSuggestedPrompts,
    isOpenPopper = _ref.isOpenPopper,
    setSelectedCategory = _ref.setSelectedCategory,
    showSubmitButton = _ref.showSubmitButton,
    _ref$submitButtonVert = _ref.submitButtonVerticalAlign,
    submitButtonVerticalAlign = _ref$submitButtonVert === void 0 ? 'center' : _ref$submitButtonVert,
    initialAdornment = _ref.initialAdornment,
    variant = _ref.variant;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isPromptFocused = _useState2[0],
    setIsPromptFocused = _useState2[1];
  var _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    prompt = _useState4[0],
    setPrompt = _useState4[1];
  var promptFormRef = useRef(null);
  var onPromptChange = function onPromptChange(e) {
    setPrompt(e.target.value);
    onChangePrompt === null || onChangePrompt === void 0 ? void 0 : onChangePrompt(e.target.value);
  };
  var handleClearPrompt = function handleClearPrompt() {
    setPrompt('');
    setSelectedCategory === null || setSelectedCategory === void 0 ? void 0 : setSelectedCategory(undefined);
    setIsPromptFocused(false);
  };
  var handleOutsideClick = React.useCallback(function (e) {
    if (promptFormRef.current && !promptFormRef.current.contains(e.target) && !isOpenPopper && !prompt) {
      setIsPromptFocused(false);
    }
  }, [prompt, isOpenPopper]);

  /**
   * useEffect to initialize the prompt when a defaultPrompt is provided
   *
   * This useEffect is necessary because:
   * 1. The defaultPrompt can come as a prop from the parent component
   * 2. We need to set the initial prompt value only if defaultPrompt has content
   * 3. It only executes when defaultPrompt changes, avoiding unnecessary updates
   * 4. Allows the component to have a predefined initial value
   */
  React.useEffect(function () {
    if (!(defaultPrompt !== null && defaultPrompt !== void 0 && defaultPrompt.trim())) {
      return;
    }
    setPrompt(defaultPrompt);
  }, [defaultPrompt]);

  /**
   * useEffect to synchronize internal state when external selectedTopic changes
   *
   * This useEffect is necessary because:
   * 1. The selectedTopic can come as a prop from the parent component
   * 2. We need to keep the internal state synchronized (prompt, selectedCategory, isPromptFocused)
   * 3. When selectedTopic changes, we must update all related states
   * 4. If there's no selectedTopic, we clear all states to reset the form
   *
   * Without this useEffect, the component wouldn't respond to external selectedTopic changes
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  React.useEffect(function () {
    if (selectedTopic) {
      setPrompt(selectedTopic.prompt);
      setSelectedCategory === null || setSelectedCategory === void 0 ? void 0 : setSelectedCategory(categories.find(function (c) {
        return 'category' in c && c.category === selectedTopic.category;
      }));
      setIsPromptFocused(true);
    } else {
      handleClearPrompt();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTopic]);
  var handleOnSubmit = function handleOnSubmit(e) {
    e === null || e === void 0 ? void 0 : e.preventDefault();
    onSubmitPrompt(_objectSpread(_objectSpread({}, selectedTopic), {}, {
      prompt: prompt,
      suggestions: initialSuggestedPrompts
    }));
    handleClearPrompt();
  };

  /**
   * useEffect to handle clicks outside the component and close the prompt focus
   *
   * This useEffect is necessary because:
   * 1. We need to detect when the user clicks outside the form
   * 2. We only close the focus if there's no popper open and no text in the prompt
   * 3. Prevents the prompt from staying focused when the user interacts with other elements
   * 4. Improves user experience by automatically closing the focus state
   *
   * The event listener is cleaned up to avoid memory leaks
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  React.useEffect(function () {
    document.addEventListener('click', handleOutsideClick);
    return function () {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [isPromptFocused, handleOutsideClick]);
  return /*#__PURE__*/_jsxs("form", {
    ref: promptFormRef,
    onSubmit: handleOnSubmit,
    className: clsx('tw-relative tw-rounded-2 tw-p-1.6 tw-w-full tw-bg-neutral-light-100 tw-flex tw-flex-col tw-justify-between tw-border tw-border-solid tw-border-neutral-light-400', className),
    children: [/*#__PURE__*/_jsxs("div", {
      className: clsx('tw-flex tw-justify-between tw-gap-1.6 tw-w-full tw-items-center', {
        'tw-pr-4.4': !cta || showSubmitButton
      }),
      children: [initialAdornment && /*#__PURE__*/_jsxs("div", {
        className: "tw-flex tw-items-center tw-gap-1.6",
        children: [initialAdornment, _div || (_div = /*#__PURE__*/_jsx("div", {
          className: "tw-h-[1.7rem] tw-w-0.1 tw-bg-neutral-light-400"
        }))]
      }), /*#__PURE__*/_jsx(HanaPromptTextareaV2, {
        customPlaceholders: customPlaceholders,
        placeholder: placeholder,
        className: promptClassName,
        prompt: prompt,
        onPromptChange: onPromptChange,
        onFocus: function onFocus() {
          return setIsPromptFocused(true);
        },
        onSubmit: handleOnSubmit,
        variant: variant,
        fixedHeight: promptClassName.includes('tw-h-')
      }), !cta && !!showSubmitButton && /*#__PURE__*/_jsx("div", {
        className: clsx('tw-absolute tw-right-1 tw-flex', {
          'tw-top-1': submitButtonVerticalAlign === 'top',
          'tw-top-[50%] tw-translate-y-[-50%]': submitButtonVerticalAlign === 'center',
          'tw-bottom-1': submitButtonVerticalAlign === 'bottom'
        }),
        children: /*#__PURE__*/_jsx(SubmitButton, {
          isLoading: isPromptSubmitLoading,
          disabled: !prompt
        })
      })]
    }), cta && /*#__PURE__*/_jsxs("div", {
      className: "tw-flex tw-items-center tw-w-full tw-justify-between tw-gap-0.8",
      children: [/*#__PURE__*/_jsx(_Fragment, {
        children: cta
      }), /*#__PURE__*/_jsx(SubmitButton, {
        isLoading: isPromptSubmitLoading,
        disabled: !prompt
      })]
    })]
  });
};